import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { getProject, fetchAttachmentBlob, getExecutionHistory } from '../services/api';
import { HiOutlinePlay } from 'react-icons/hi';
import { FiCheckCircle, FiXCircle, FiAlertTriangle, FiClock, FiMinusCircle, FiChevronDown, FiChevronRight, FiMessageSquare, FiActivity } from 'react-icons/fi';

const STATUS_CONFIG = {
  NT: { label: 'Non Teste', icon: FiMinusCircle, color: 'var(--gray-400)', bg: 'var(--gray-100)' },
  EC: { label: 'En Cours', icon: FiClock, color: 'var(--info)', bg: 'var(--info-light)' },
  OK: { label: 'Reussi', icon: FiCheckCircle, color: 'var(--green-600)', bg: 'var(--green-100)' },
  KO: { label: 'Echoue', icon: FiXCircle, color: 'var(--danger)', bg: 'var(--danger-light)' },
  BL: { label: 'Bloque', icon: FiAlertTriangle, color: 'var(--warning)', bg: 'var(--warning-light)' },
};

function AttachmentThumb({ projectId, testCaseId, attachment }) {
  const [src, setSrc] = React.useState(null);
  React.useEffect(() => {
    let revoked = false;
    if (projectId && testCaseId && attachment?.id) {
      fetchAttachmentBlob(projectId, testCaseId, attachment.id).then(url => {
        if (!revoked) setSrc(url);
      }).catch(() => {});
    }
    return () => { revoked = true; if (src) URL.revokeObjectURL(src); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, testCaseId, attachment?.id]);
  if (!src) return null;
  return <img src={src} alt={attachment.filename} className="attachment-thumb" title={attachment.filename} />;
}

function HistoryTimeline({ projectId, testCaseId }) {
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getExecutionHistory(projectId, testCaseId)
      .then(({ data }) => setHistory(data))
      .catch(() => setHistory([]))
      .finally(() => setLoading(false));
  }, [projectId, testCaseId]);

  if (loading) return <div className="text-muted" style={{ fontSize: '0.8rem', padding: '8px 0' }}>Chargement historique...</div>;
  if (!history || history.length === 0) return <div className="text-muted" style={{ fontSize: '0.8rem', padding: '8px 0' }}>Aucun historique d'execution.</div>;

  const formatDate = (d) => {
    if (!d) return '';
    const date = new Date(d);
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })
      + ' ' + date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="history-timeline">
      {history.map((entry, idx) => {
        const cfg = STATUS_CONFIG[entry.statut] || STATUS_CONFIG.NT;
        const prevCfg = entry.previous_statut ? (STATUS_CONFIG[entry.previous_statut] || STATUS_CONFIG.NT) : null;
        const Icon = cfg.icon;
        return (
          <div key={entry.id} className="history-entry">
            <div className="history-line">
              {idx < history.length - 1 && <div className="history-connector"></div>}
              <div className="history-dot" style={{ background: cfg.color }}></div>
            </div>
            <div className="history-content">
              <div className="history-header">
                <span className="history-badge" style={{ background: cfg.bg, color: cfg.color }}>
                  <Icon style={{ fontSize: '0.7rem' }} /> {cfg.label}
                </span>
                {prevCfg && (
                  <span className="history-transition">
                    <span style={{ color: prevCfg.color }}>{prevCfg.label}</span>
                    <span className="history-arrow"> → </span>
                    <span style={{ color: cfg.color }}>{cfg.label}</span>
                  </span>
                )}
              </div>
              <div className="history-meta">
                <span className="history-user">{entry.executed_by_username || 'Systeme'}</span>
                <span className="history-date">{formatDate(entry.executed_at)}</span>
                {entry.environment && <span className="history-env">{entry.environment}</span>}
                {entry.campaign_name && <span className="history-campaign">{entry.campaign_name}</span>}
              </div>
              {entry.commentaire && (
                <div className="history-comment">{entry.commentaire}</div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function TestExecution() {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [filter, setFilter] = useState('ALL');
  const [expandedReqs, setExpandedReqs] = useState({});
  const [activeTC, setActiveTC] = useState(null);
  const [showHistory, setShowHistory] = useState(null);

  const loadProject = useCallback(async () => {
    const { data } = await getProject(id);
    setProject(data);
    const expanded = {};
    data.requirements.forEach(r => { expanded[r.id] = true; });
    setExpandedReqs(prev => Object.keys(prev).length === 0 ? expanded : prev);
  }, [id]);

  useEffect(() => { loadProject(); }, [loadProject]);

  if (!project) return (
    <div className="loading-spinner">
      <output className="spinner-border"></output>
      <span>Chargement...</span>
    </div>
  );

  const allTC = project.requirements.flatMap(r => r.test_cases);
  const stats = { NT: 0, EC: 0, OK: 0, KO: 0, BL: 0 };
  allTC.forEach(tc => { stats[tc.statut] = (stats[tc.statut] || 0) + 1; });
  const total = allTC.length;
  const executed = stats.OK + stats.KO;
  const executedPct = total > 0 ? ((executed / total) * 100).toFixed(0) : 0;
  const successPct = executed > 0 ? ((stats.OK / executed) * 100).toFixed(0) : 0;

  const toggleReq = (reqId) => {
    setExpandedReqs(prev => ({ ...prev, [reqId]: !prev[reqId] }));
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h2><HiOutlinePlay className="me-2" style={{ color: 'var(--green-500)' }} />Execution des Tests</h2>
          <p className="text-muted mb-0" style={{ fontSize: '0.85rem' }}>{project.name} — Vue de suivi (lecture seule)</p>
        </div>
      </div>

      {/* Stats bar */}
      <div className="exec-stats-bar mb-3">
        <button type="button" className={`exec-stat-chip ${filter === 'ALL' ? 'active' : ''}`} style={{ background: 'none', border: 'none', font: 'inherit', color: 'inherit', cursor: 'pointer', padding: 0, textAlign: 'left' }} onClick={() => setFilter('ALL')}>
          <span className="exec-stat-chip-count">{total}</span> Total
        </button>
        {Object.entries(STATUS_CONFIG).map(([key, cfg]) => {
          const Icon = cfg.icon;
          return (
            <button type="button" key={key} className={`exec-stat-chip ${filter === key ? 'active' : ''}`}
                 style={filter === key ? { borderColor: cfg.color, background: cfg.bg, color: cfg.color, font: 'inherit', cursor: 'pointer', padding: 0, textAlign: 'left' } : { background: 'none', border: 'none', font: 'inherit', color: 'inherit', cursor: 'pointer', padding: 0, textAlign: 'left' }}
                 onClick={() => setFilter(filter === key ? 'ALL' : key)}>
              <Icon style={{ fontSize: '0.85rem' }} />
              <span className="exec-stat-chip-count">{stats[key]}</span> {cfg.label}
            </button>
          );
        })}
      </div>

      {/* Progress */}
      <div className="exec-progress-card mb-2">
        <div className="d-flex justify-content-between mb-2">
          <span className="fw-500" style={{ fontSize: '0.82rem' }}>Progression</span>
          <span className="text-muted" style={{ fontSize: '0.78rem' }}>{executedPct}% execute  |  {successPct}% reussi</span>
        </div>
        <div className="test-progress" style={{ height: '10px' }}>
          {total > 0 && <>
            <div className="progress-ok" style={{ width: `${(stats.OK/total)*100}%` }}></div>
            <div className="progress-ko" style={{ width: `${(stats.KO/total)*100}%` }}></div>
            <div className="progress-ec" style={{ width: `${(stats.EC/total)*100}%` }}></div>
            <div className="progress-bl" style={{ width: `${(stats.BL/total)*100}%` }}></div>
            <div className="progress-nt" style={{ width: `${(stats.NT/total)*100}%` }}></div>
          </>}
        </div>
      </div>

      {/* Test Cases grouped by requirement */}
      {project.requirements.map(req => {
        const reqTCs = filter === 'ALL' ? req.test_cases : req.test_cases.filter(tc => tc.statut === filter);
        if (reqTCs.length === 0) return null;
        const isExpanded = expandedReqs[req.id];

        return (
          <div key={req.id} className="exec-req-group mb-3">
            <button type="button" className="exec-req-header" style={{ background: 'none', border: 'none', font: 'inherit', color: 'inherit', cursor: 'pointer', padding: 0, textAlign: 'left', width: '100%' }} onClick={() => toggleReq(req.id)}>
              {isExpanded ? <FiChevronDown className="me-2" /> : <FiChevronRight className="me-2" />}
              <span className="req-id">{req.exg_id}</span>
              <span className="fw-500 flex-grow-1">{req.exigence}</span>
              <span className="meta-tag">{reqTCs.length} cas</span>
            </button>

            {isExpanded && (
              <div className="exec-tc-list">
                {reqTCs.map(tc => {
                  const cfg = STATUS_CONFIG[tc.statut];
                  const Icon = cfg.icon;
                  const isActive = activeTC === tc.id;

                  return (
                    <div key={tc.id} className={`exec-tc-item ${isActive ? 'exec-tc-active' : ''}`}>
                      <button type="button" className="exec-tc-row" style={{ background: 'none', border: 'none', font: 'inherit', color: 'inherit', cursor: 'pointer', padding: 0, textAlign: 'left', width: '100%' }} onClick={() => setActiveTC(isActive ? null : tc.id)}>
                        <span className="exec-tc-status" style={{ background: cfg.bg, color: cfg.color }}>
                          <Icon />
                        </span>
                        <div className="exec-tc-info">
                          <span className="exec-tc-id">{tc.ct_id}</span>
                          <span className="exec-tc-name">{tc.cas_test}</span>
                        </div>
                        <div className="d-flex align-items-center gap-1">
                          {tc.commentaire && (
                            <span className="comment-indicator" title={tc.commentaire}>
                              <FiMessageSquare />
                            </span>
                          )}
                          <span className="exec-tc-badge" style={{ background: cfg.bg, color: cfg.color }}>
                            {cfg.label}
                          </span>
                        </div>
                      </button>

                      {isActive && (
                        <div className="exec-tc-detail">
                          <div className="exec-tc-sections">
                            {tc.prerequis && (
                              <div className="exec-tc-section">
                                <div className="exec-tc-section-label">Prerequis</div>
                                <div className="exec-tc-section-content">{tc.prerequis}</div>
                              </div>
                            )}
                            <div className="exec-tc-section">
                              <div className="exec-tc-section-label">Actions a realiser</div>
                              <div className="exec-tc-section-content action-text">{tc.action}</div>
                            </div>
                            <div className="exec-tc-section">
                              <div className="exec-tc-section-label">Resultat attendu</div>
                              <div className="exec-tc-section-content">{tc.resultat_attendu}</div>
                            </div>
                            {tc.commentaire && (
                              <div className="exec-tc-section" style={{ borderLeft: '3px solid var(--danger)', paddingLeft: '10px' }}>
                                <div className="exec-tc-section-label d-flex align-items-center gap-2">
                                  <FiMessageSquare /> Commentaire
                                </div>
                                <div className="exec-tc-section-content">{tc.commentaire}</div>
                              </div>
                            )}
                            {tc.attachments && tc.attachments.length > 0 && (
                              <div className="exec-tc-section">
                                <div className="exec-tc-section-label d-flex align-items-center gap-1">
                                  <FiMessageSquare /> Pieces jointes ({tc.attachments.length})
                                </div>
                                <div className="d-flex flex-wrap gap-2 mt-1">
                                  {tc.attachments.map(att => (
                                    <div key={att.id} className="attachment-item">
                                      <AttachmentThumb projectId={id} testCaseId={tc.id} attachment={att} />
                                      <div className="attachment-name">{att.filename}</div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Historique d'execution */}
                            <div className="exec-tc-section">
                              <button
                                type="button"
                                className="exec-tc-section-label d-flex align-items-center gap-2"
                                style={{ cursor: 'pointer', userSelect: 'none', background: 'none', border: 'none', font: 'inherit', color: 'inherit', padding: 0, textAlign: 'left' }}
                                onClick={(e) => { e.stopPropagation(); setShowHistory(showHistory === tc.id ? null : tc.id); }}
                              >
                                <FiActivity /> Historique d'execution
                                {showHistory === tc.id ? <FiChevronDown style={{ fontSize: '0.75rem' }} /> : <FiChevronRight style={{ fontSize: '0.75rem' }} />}
                              </button>
                              {showHistory === tc.id && (
                                <HistoryTimeline projectId={id} testCaseId={tc.id} />
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}

      {total === 0 && (
        <div className="empty-state">
          <div className="empty-state-icon"><HiOutlinePlay /></div>
          <p>Aucun cas de test. Ajoutez des exigences et cas de test d'abord.</p>
        </div>
      )}
    </div>
  );
}
